import { useApiGetter, ExtensionService, useApiEffect, useSnackbar, AdminService } from '@localstack/services';
import { AdminPermission, EnrichedExtension } from '@localstack/types';
import { Grid, Skeleton } from '@mui/material';
import { ReactElement } from 'react';
import { useParams } from 'react-router-dom';

import { AdminLayout } from '~/layouts';
import { ExtensionSubmissionCard } from '~/views/customer/Extensions/components/ExtensionSubmissionCard';

import { ExtensionCard } from '~/views/customer/Extensions/components/ExtensionCard';

import { useAuthProvider } from '~/hooks/useAuthProvider';

import { SmallOrgDetails } from './SmallOrgDetails';
import { ExtensionInstallCard } from './ExtensionInstallCard';

export const AdminExtension = (): ReactElement => {
  const { can } = useAuthProvider();
  const { id: extensionId } = useParams<'id'>() as { id: string };

  const { showSnackbar } = useSnackbar();

  const { data: extension, isLoading: isExtensionLoading } = useApiGetter(ExtensionService, 'getExtension', [
    extensionId,
  ]);

  const { updateExtensionAdmin } = useApiEffect(AdminService, ['updateExtensionAdmin'], {
    revalidateOtherClient: {
      client: [ExtensionService] as const,
      methods: ['listExtensions', 'listExtensionsMarketplace', 'getExtension'],
    },
  });

  const { data: organization, isLoading: isOrgLoading } = useApiGetter(
    AdminService,
    'getOrganization',
    [extension?.org_id],
    {
      enable: !!extension,
      suppressErrors: true,
      swrOverrides: { shouldRetryOnError: false },
    },
  );

  const handleChangesSubmit = async (data: EnrichedExtension) => {
    const { logoFile, ...modifiedExtension } = data;

    try {
      const response = await updateExtensionAdmin(modifiedExtension.id, modifiedExtension);
      if (response?.pre_signed_url && logoFile) {
        await fetch(response.pre_signed_url, {
          method: 'PUT',
          headers: {
            'Content-Type': logoFile?.type ?? '',
          },
          body: logoFile,
        });
      }
      showSnackbar({
        message: 'Extension successfully updated',
        severity: 'success',
      });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <AdminLayout documentTitle="Extension Details" title={extension?.display_name ?? extensionId}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {isExtensionLoading ? (
            <Skeleton height={645} variant="rectangular" />
          ) : (
            <ExtensionSubmissionCard
              extension={extension ?? undefined}
              onSubmit={handleChangesSubmit}
              submitText="Save changes"
              cardTitle={extension?.display_name}
              editing
              showPlatformAdminFields
              disableInteraction={!can(AdminPermission.EXTENSIONS_WRITE)}
            />
          )}
        </Grid>
        <Grid item xs={6} lg={6}>
          {isOrgLoading || isExtensionLoading ? (
            <Skeleton height={210} variant="rectangular" />
          ) : (
            <ExtensionInstallCard extension={extension} />
          )}
        </Grid>
        <Grid item xs={6}>
          {isExtensionLoading ? (
            <Skeleton height={205} variant="rectangular" />
          ) : (
            extension && <ExtensionCard extension={extension} size="medium" isUpdating={isExtensionLoading} />
          )}
        </Grid>
        <Grid item xs={6} lg={6}>
          {isOrgLoading || isExtensionLoading ? (
            <Skeleton height={275} variant="rectangular" />
          ) : (
            <SmallOrgDetails
              organization={organization ?? undefined}
              alertMessage="No organization attached to this extension"
            />
          )}
        </Grid>
      </Grid>
    </AdminLayout>
  );
};
